class GiftWithPurchase {
  constructor() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this))
    if (this.data?.autoAddVariantId) {
      this._checkAutoAdd()
    }
  }

  get dataEl() {
    return document.querySelector('script[data-gift-with-purchase-data]')
  }

  get shouldRun() {
    return !!this.dataEl
  }

  get data() {
    return JSON.parse(this.dataEl.textContent.trim())
  }

  get _gwpDeclinedKey() {
    return `gwp-declined-${this.data?.autoAddVariantId}`
  }

  get _gwpDeclined() {
    return sessionStorage.getItem(this._gwpDeclinedKey) === '1'
  }

  set _gwpDeclined(val) {
    if (val) {
      sessionStorage.setItem(this._gwpDeclinedKey, '1')
    } else {
      sessionStorage.removeItem(this._gwpDeclinedKey)
    }
  }

  async onCartUpdate(event) {
    if (!this.shouldRun) return
    if (event?.source === 'gwp') return

    const cart =
      event?.cartData != null && 'discount_codes' in event.cartData
        ? event.cartData
        : await fetch('/cart.js').then((r) => r.json())

    const cartIsValid = this.getCartIsValid(cart)

    if (!cartIsValid) {
      await this.removeGwpItems(cart)
      this._gwpDeclined = false
      this._hadGwp = false
    }

    if (cartIsValid && this.data?.autoAddVariantId) {
      const hasGwp = cart.items.some((item) => item.properties?._gwp)

      if (hasGwp) {
        this._gwpDeclined = false
      } else if (this._hadGwp && event?.source !== 'gwp-remove') {
        this._gwpDeclined = true
      }
      this._hadGwp = hasGwp

      if (!hasGwp && !this._gwpDeclined && !this._autoAddInflight) {
        this._autoAddInflight = true
        this._addGwpVariant(this.data.autoAddVariantId).finally(() => {
          this._autoAddInflight = false
        })
        return
      }
    }

    if (this.data.applyCodeAutomatically) {
      const alreadyHasCode = this.getAlreadyHasDiscountCode(cart)
      if (!cartIsValid && alreadyHasCode) {
        await this.removeDiscountCode(cart)
      } else if (cartIsValid && !alreadyHasCode) {
        await this.addDiscountCode(cart)
      }
    }
  }

  getCartIsValid(cart) {
    const { discountType, itemType, thresholdAmount, thresholdQuantity, qualifyingProductIds } =
      this.data

    if (itemType === 'Collection' || !qualifyingProductIds?.length) {
      return this.data.passedThreshold
    }

    const qualifyingItems = cart.items.filter(
      (item) => !item.properties?._gwp && qualifyingProductIds.includes(item.product_id),
    )

    if (discountType === 'MinimumPurchase') {
      return (
        qualifyingItems.reduce((sum, item) => sum + item.final_line_price, 0) >= thresholdAmount
      )
    }

    if (discountType === 'MinimumQuantity') {
      return qualifyingItems.reduce((sum, item) => sum + item.quantity, 0) >= thresholdQuantity
    }

    return this.data.passedThreshold
  }

  getAlreadyHasDiscountCode(cart) {
    return cart.discount_codes?.some((dc) => dc.code === this.data.discountCode) ?? false
  }

  getCurrentAppliedDiscountCodes(cart) {
    return (cart.discount_codes || []).map((dc) => dc.code)
  }

  async removeGwpItems(cart) {
    const gwpItems = cart.items.filter((item) => item.properties?._gwp)
    if (!gwpItems.length) return

    for (const item of gwpItems) {
      const body = JSON.stringify({
        id: item.key,
        quantity: 0,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      })
      const data = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }).then((r) => r.json())

      if (data.sections) {
        this.renderCartDrawerSections(data.sections, data.item_count === 0)
      }
    }

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp-remove' })
  }

  renderCartDrawerSections(sections, isEmpty) {
    const drawerHtml = sections['cart-drawer']
    if (drawerHtml) {
      // Replace .drawer__inner wholesale — this includes the empty state,
      // free-shipping banner, items, and footer so all states render correctly.
      const cartDrawerEl = document.getElementById('CartDrawer')
      const dom = new DOMParser().parseFromString(drawerHtml, 'text/html')
      const source = dom.querySelector('.drawer__inner')
      if (cartDrawerEl && source) {
        cartDrawerEl.querySelector('.drawer__inner').innerHTML = source.innerHTML
      }

      // Sync is-empty on the outer <cart-drawer> element.
      const cartDrawerWrapper = document.querySelector('cart-drawer')
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isEmpty)
    }

    const bubbleHtml = sections['cart-icon-bubble']
    if (bubbleHtml) {
      const bubbleEl = document.getElementById('cart-icon-bubble')
      if (bubbleEl) {
        const dom = new DOMParser().parseFromString(bubbleHtml, 'text/html')
        const source = dom.querySelector('.shopify-section')
        if (source) bubbleEl.innerHTML = source.innerHTML
      }
    }
  }

  async addDiscountCode(cart) {
    const codes = [
      ...this.getCurrentAppliedDiscountCodes(cart).filter((c) => c !== this.data.discountCode),
      this.data.discountCode,
    ]
    const fd = new FormData()
    fd.append('discount', codes.join(','))
    const data = await fetch('/cart/update.js', { method: 'POST', body: fd }).then((r) => r.json())
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp', cartData: data })
  }

  async removeDiscountCode(cart) {
    const codes = this.getCurrentAppliedDiscountCodes(cart).filter(
      (c) => c !== this.data.discountCode,
    )
    const fd = new FormData()
    fd.append('discount', codes.join(','))
    const data = await fetch('/cart/update.js', { method: 'POST', body: fd }).then((r) => r.json())
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gwp', cartData: data })
  }

  async _checkAutoAdd() {
    if (!this.data?.autoAddVariantId || this._autoAddInflight) return
    const cart = await fetch('/cart.js').then((r) => r.json())
    const valid = this.getCartIsValid(cart)
    const hasGwp = cart.items.some((item) => item.properties?._gwp)
    this._hadGwp = hasGwp
    if (!valid || this._autoAddInflight || hasGwp || this._gwpDeclined) return
    this._autoAddInflight = true
    this._addGwpVariant(this.data.autoAddVariantId).finally(() => {
      this._autoAddInflight = false
    })
  }

  async _addGwpVariant(variantId) {
    try {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification')
      const sections = cart ? cart.getSectionsToRender().map((s) => s.id) : []
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _gwp: 'true' },
          sections,
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json())
      if (response.status) { console.error('[GWP] add failed', response); return }
      if (!cart) { window.location = window.routes.cart_url; return }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-with-purchase-item', cartData: response })
      cart.renderContents(response)

      const cartCountEl = document.querySelector('.cart-count-bubble [aria-hidden="true"]')
      const toastCounterEl = document.querySelector('[data-cart-notice-el="counter"]')
      if (toastCounterEl && cartCountEl) {
        toastCounterEl.textContent = `(${cartCountEl.textContent.trim()})`
      }
    } catch (err) {
      console.error('[GWP] _addGwpVariant error', err)
    }
  }
}

if (!customElements.get('gift-with-purchase-item')) {
  customElements.define(
    'gift-with-purchase-item',
    class GiftWithPurchaseItem extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-gift-with-purchase-item-el="form"]')
        this.idInput = this.querySelector('[data-gift-with-purchase-item-el="idInput"]')
        this.productOptions = [
          ...this.querySelectorAll('[data-gift-with-purchase-item-el="product-option"]'),
        ]
        this.valueDisplays = [
          ...this.querySelectorAll('[data-gift-with-purchase-item-el="option-value-display"]'),
        ]

        if (this.form) {
          this.form.addEventListener('change', this.onFormChange.bind(this))
          this.form.addEventListener('submit', (e) => { e.preventDefault(); this.onAddToCart() })
        }
      }

      async onAddToCart() {
        if (this.busy || !this.form) return
        this.busy = true

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification')

        this.setAttribute('aria-busy', 'true')

        const formData = new FormData(this.form)
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((s) => s.id).join(','))
          formData.append('sections_url', window.location.pathname)
          cart.setActiveElement(document.activeElement)
        }

        const config = fetchConfig('javascript')
        config.headers['X-Requested-With'] = 'XMLHttpRequest'
        delete config.headers['Content-Type']
        config.body = formData

        let hasError = false
        try {
          const response = await fetch(routes.cart_add_url, config).then((r) => r.json())

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'gift-with-purchase-item',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            })
            hasError = true
            return
          }

          if (!cart) {
            window.location = window.routes.cart_url
            return
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'gift-with-purchase-item',
            productVariantId: formData.get('id'),
            cartData: response,
          })

          cart.renderContents(response)
        } catch (err) {
          console.error(err)
          hasError = true
        } finally {
          this.setAttribute('aria-busy', 'false')
          if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty')
          this.busy = false
        }
      }

      async onFormChange(e) {
        e.stopPropagation()
        const chosenOption = Array.from(e.target.options).find((o) => o.value === e.target.value)
        if (!chosenOption) return

        if (this.idInput) {
          this.idInput.value = chosenOption.getAttribute('data-variant-id')
        }

        const selectIndex = this.productOptions.indexOf(e.target)
        const valueDisplay = this.valueDisplays[selectIndex]
        if (valueDisplay) valueDisplay.textContent = chosenOption.text

        if (this.allValuesAreFilled) {
          await this.onAddToCart()
          return
        }

        await this.updateVariantAvailability()
      }

      async updateVariantAvailability() {
        const optionValues = this.productOptions.map((select) => {
          if (select.value === '') {
            return select.getAttribute('data-default-option-value-id')
          }
          const selected = Array.from(select.options).find((o) => o.value === select.value)
          return selected?.getAttribute('data-option-id')
        })

        const handle = this.getAttribute('data-product-handle')
        const url = `/products/${handle}?option_values=${optionValues.join(',')}&sections[]=product-gift-with-purchase-unclaimed`

        const data = await fetch(url).then((r) => r.json())
        const dom = new DOMParser().parseFromString(
          data['product-gift-with-purchase-unclaimed'],
          'text/html',
        )

        const newOptions = [
          ...dom.querySelectorAll('[data-gift-with-purchase-item-el="product-option"]'),
        ]

        for (const newSelect of newOptions) {
          const currentSelect = this.productOptions.find(
            (s) => s.getAttribute('name') === newSelect.getAttribute('name'),
          )
          if (!currentSelect) continue

          for (let i = 0; i < currentSelect.options.length; i++) {
            const currentOpt = currentSelect.options[i]
            const newOpt = newSelect.options[i]
            if (!newOpt || currentOpt.value === '') continue

            currentOpt.setAttribute('data-variant-id', newOpt.getAttribute('data-variant-id') || '')
            if (!currentOpt.hasAttribute('data-perma-disabled')) {
              currentOpt.disabled = newOpt.disabled
            }
          }
        }
      }

      get allValuesAreFilled() {
        return this.productOptions.every((s) => s.value !== '')
      }
    },
  )
}

if (!customElements.get('gift-with-purchase-pdp')) {
  customElements.define(
    'gift-with-purchase-pdp',
    class GiftWithPurchasePDP extends HTMLElement {
      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this),
        )
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber()
        }
      }

      async onCartUpdate(event) {
        if (event?.source === 'gwp') return
        const sectionId = this.closest('[data-section]')?.dataset.section
        if (!sectionId) return

        const url = `${window.location.pathname}?sections[]=${sectionId}`
        const data = await fetch(url).then((r) => r.json())
        if (!data[sectionId]) return

        const dom = new DOMParser().parseFromString(data[sectionId], 'text/html')
        const fresh = dom.querySelector('gift-with-purchase-pdp')
        if (fresh) this.innerHTML = fresh.innerHTML
      }
    },
  )
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('script[data-gift-with-purchase-data]')) {
    new GiftWithPurchase()
  }
})
